/**
 * AnnouncementsModule - Admin component for managing special announcements
 * Announcements appear as slides in the Rules Presentation
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  AlertTriangle,
  Calendar,
  Heart,
  PartyPopper,
  Loader2,
  X,
  Save,
  Image,
  Tv
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Announcement types
const ANNOUNCEMENT_TYPES = [
  { id: 'general', label: 'General Announcement', icon: Bell, color: 'bg-blue-500' },
  { id: 'urgent', label: 'Urgent Notice', icon: AlertTriangle, color: 'bg-red-500' },
  { id: 'event', label: 'Upcoming Event', icon: Calendar, color: 'bg-purple-500' },
  { id: 'prayer', label: 'Prayer Request', icon: Heart, color: 'bg-amber-500' },
  { id: 'birthday', label: 'Birthday/Celebration', icon: PartyPopper, color: 'bg-pink-500' },
  { id: 'thanksgiving', label: 'Thanksgiving', icon: Heart, color: 'bg-green-500' }
];

const ANNOUNCEMENTS_API = `${API}/special-announcements`;

const AnnouncementsModule = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flyers, setFlyers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'general',
    priority: 0,
    is_active: true,
    expires_at: '',
    flyer_id: ''
  });

  useEffect(() => {
    loadAnnouncements();
    loadFlyers();
  }, []);

  const loadFlyers = async () => {
    try {
      const res = await axios.get(`${API}/flyers`, { params: { active_only: false } });
      setFlyers(res.data || []);
    } catch (e) { console.error('Error loading flyers:', e); }
  };

  const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const loadAnnouncements = async () => {
    try {
      const res = await axios.get(`${ANNOUNCEMENTS_API}?include_expired=true`);
      setAnnouncements(res.data.announcements || []);
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
    setLoading(false);
  };

  const handleOpenModal = (announcement = null) => {
    if (announcement) {
      setEditingId(announcement.id);
      setFormData({
        title: announcement.title,
        content: announcement.content,
        type: announcement.type,
        priority: announcement.priority || 0,
        is_active: announcement.is_active,
        expires_at: announcement.expires_at ? announcement.expires_at.split('T')[0] : '',
        flyer_id: announcement.flyer_id || ''
      });
    } else {
      setEditingId(null);
      setFormData({
        title: '',
        content: '',
        type: 'general',
        priority: 0,
        is_active: true,
        expires_at: '',
        flyer_id: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.content) {
      alert('Please fill in title and content');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };

      if (editingId) {
        await axios.put(`${ANNOUNCEMENTS_API}/${editingId}`, payload);
      } else {
        await axios.post(`${ANNOUNCEMENTS_API}`, payload);
      }

      setShowModal(false);
      loadAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert('Failed to save announcement');
    }
    setSaving(false);
  };

  const handleToggle = async (id) => {
    try {
      await axios.patch(`${ANNOUNCEMENTS_API}/${id}/toggle`);
      loadAnnouncements();
    } catch (error) {
      console.error('Error toggling announcement:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;

    try {
      await axios.delete(`${ANNOUNCEMENTS_API}/${id}`);
      loadAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  const handleBroadcast = async (announcement) => {
    // Push the announcement (with attached flyer) to the Live Display & Mobile Remote.
    try {
      let flyer = null;
      if (announcement.flyer_id) {
        flyer = flyers.find((f) => f.id === announcement.flyer_id) || null;
        if (!flyer) {
          try {
            const r = await axios.get(`${API}/flyers/${announcement.flyer_id}`);
            flyer = r.data || null;
          } catch (e) { /* ignore — broadcast without flyer */ }
        }
      }
      const payload = {
        title: announcement.title,
        description: announcement.content || '',
        type: announcement.type || 'general',
        flyer_url: flyer?.image_url || '',
        flyer: flyer ? { title: flyer.title, image_url: flyer.image_url } : null,
      };
      await axios.post(`${API}/display/show/announcement`, payload);
      alert('Announcement is now live on the screens & remote.');
    } catch (error) {
      console.error('Error broadcasting announcement:', error);
      alert('Failed to broadcast. Please try again.');
    }
  };

  const getTypeInfo = (typeId) => {
    return ANNOUNCEMENT_TYPES.find(t => t.id === typeId) || ANNOUNCEMENT_TYPES[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Special Announcements</h2>
          <p className="text-slate-500 text-sm mt-1">
            Create announcements that appear as slides in the presentation
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
          data-testid="add-announcement-btn"
        >
          <Plus className="w-4 h-4" />
          Add Announcement
        </button>
      </div>

      {/* Announcements List */}
      {announcements.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
          <Bell className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">No Announcements</h3>
          <p className="text-slate-400 mb-6">Create your first announcement to display in the presentation</p>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-colors"
          >
            Create Announcement
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {announcements.map((announcement) => {
            const typeInfo = getTypeInfo(announcement.type);
            const IconComponent = typeInfo.icon;
            const isExpired = announcement.expires_at && new Date(announcement.expires_at) < new Date();

            return (
              <motion.div
                key={announcement.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden ${
                  !announcement.is_active || isExpired ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-start gap-4 p-5">
                  {/* Type Badge */}
                  <div className={`p-3 rounded-xl ${typeInfo.color} text-white flex-shrink-0`}>
                    <IconComponent className="w-6 h-6" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-800 text-lg">{announcement.title}</h3>
                          {!announcement.is_active && (
                            <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full">
                              Hidden
                            </span>
                          )}
                          {isExpired && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">
                              Expired
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 mb-2 line-clamp-2">{announcement.content}</p>
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${typeInfo.color} text-white`}>
                            {typeInfo.label}
                          </span>
                          {announcement.expires_at && (
                            <span>Expires: {formatDate(announcement.expires_at)}</span>
                          )}
                          {announcement.priority > 0 && (
                            <span>Priority: {announcement.priority}</span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleBroadcast(announcement)}
                          className="p-2 rounded-lg bg-purple-100 text-purple-600 hover:bg-purple-200 transition-colors"
                          title="Broadcast to Live Display & Remote"
                          data-testid={`broadcast-announcement-${announcement.id}`}
                        >
                          <Tv className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggle(announcement.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            announcement.is_active
                              ? 'bg-green-100 text-green-600 hover:bg-green-200'
                              : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                          }`}
                          title={announcement.is_active ? 'Hide' : 'Show'}
                        >
                          {announcement.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleOpenModal(announcement)}
                          className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(announcement.id)}
                          className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-slate-100">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingId ? 'Edit Announcement' : 'New Announcement'}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 space-y-4">
                {/* Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
                  <div className="grid grid-cols-3 gap-2">
                    {ANNOUNCEMENT_TYPES.map((type) => {
                      const IconComponent = type.icon;
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, type: type.id })}
                          className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                            formData.type === type.id
                              ? `border-blue-500 ${type.color} text-white`
                              : 'border-slate-200 text-slate-600 hover:border-slate-300'
                          }`}
                        >
                          <IconComponent className="w-5 h-5" />
                          <span className="text-xs font-medium text-center leading-tight">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Announcement title"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="announcement-title-input"
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Content *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Enter your announcement message..."
                    rows={4}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    data-testid="announcement-content-input"
                  />
                </div>

                {/* Priority & Expiry */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                    <input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-400 mt-1">Higher = shown first</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Expires On</label>
                    <input
                      type="date"
                      value={formData.expires_at}
                      onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-slate-400 mt-1">Leave empty for no expiry</p>
                  </div>
                </div>

                {/* Attach Flyer */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <span className="flex items-center gap-1.5"><Image className="w-4 h-4" /> Attach Flyer (optional)</span>
                  </label>
                  {flyers.length > 0 ? (
                    <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto p-1">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, flyer_id: '' })}
                        className={`p-2 rounded-xl border-2 text-center transition-all ${
                          !formData.flyer_id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <X className="w-5 h-5 mx-auto text-slate-400 mb-1" />
                        <span className="text-xs text-slate-500">None</span>
                      </button>
                      {flyers.map((flyer) => (
                        <button
                          key={flyer.id}
                          type="button"
                          onClick={() => setFormData({ ...formData, flyer_id: flyer.id })}
                          data-testid={`select-flyer-${flyer.id}`}
                          className={`rounded-xl border-2 overflow-hidden transition-all ${
                            formData.flyer_id === flyer.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <img src={resolveImageUrl(flyer.image_url)} alt={flyer.title}
                            className="w-full h-16 object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                          <p className="text-xs font-medium text-slate-700 p-1 truncate">{flyer.title}</p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 bg-slate-50 rounded-xl p-3">No flyers uploaded yet. Upload flyers in Media &gt; Flyers first.</p>
                  )}
                </div>

                {/* Active Toggle */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                    Show in presentation
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3 p-5 border-t border-slate-100">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                  data-testid="save-announcement-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AnnouncementsModule;
